import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/utils/dateFormat";
import {
  Home as HomeIcon,
  Calendar,
  Target,
  Trophy,
  ArrowRight,
  Megaphone,
  CalendarClock,
  Info,
  Settings as SettingsIcon,
  UserPlus,
  Clock,
  CheckCircle,
} from "lucide-react";

const API = "/api";

// Turn plain http(s):// URLs in a string into clickable anchors (whitespace preserved).
function linkify(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p)) {
      return (
        <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-[#1B3B6F] underline hover:opacity-80 break-words">
          {p}
        </a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function ClubHome() {
  const { user, isAdmin, getAuthHeader, checkAuth } = useAuth();
  const { settings } = useSettings();
  const [todayData, setTodayData] = useState({ events: [] });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shooterDialogOpen, setShooterDialogOpen] = useState(false);
  const [shooterForm, setShooterForm] = useState({ shooter_id: "", phone: "", pin: "" });
  const [shooterSubmitting, setShooterSubmitting] = useState(false);

  const handleApplyAsShooter = async (e) => {
    e.preventDefault();
    setShooterSubmitting(true);
    try {
      await axios.post(
        `${API}/shooters/request`,
        {
          name: user?.name || "",
          email: user?.email || "",
          phone: shooterForm.phone || null,
          shooter_id: shooterForm.shooter_id || null,
          pin: shooterForm.pin || null,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Application submitted! An admin will review it shortly.");
      setShooterDialogOpen(false);
      setShooterForm({ shooter_id: "", phone: "", pin: "" });
      if (checkAuth) await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to submit application");
    } finally {
      setShooterSubmitting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const t = Date.now();
        const [todayRes, eventsRes, annRes] = await Promise.all([
          axios.get(`${API}/events/today?t=${t}`).catch(() => ({ data: { events: [] } })),
          axios.get(`${API}/events?t=${t}`).catch(() => ({ data: [] })),
          axios.get(`${API}/announcements?t=${t}`).catch(() => ({ data: [] })),
        ]);
        setTodayData(todayRes.data || { events: [] });

        // Upcoming events = start_date >= today, sorted asc, top 3
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const upcoming = (eventsRes.data || [])
          .filter((ev) => {
            if (!ev.start_date) return false;
            const d = new Date(ev.start_date);
            return !isNaN(d.getTime()) && d >= startOfToday && ev.status !== "cancelled";
          })
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .slice(0, 4);
        setUpcomingEvents(upcoming);

        setAnnouncements((annRes.data || []).slice(0, 3));
      } catch (e) {
        console.error("Home load failed", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const homeTitle = settings.home_title || `Welcome to ${settings.organization_name || "the Club"}`;
  const hasCustomContent = !!(settings.home_content && settings.home_content.trim().length);

  return (
    <div data-testid="club-home-page" className="space-y-6">
      {/* Banner image */}
      {settings.home_image_url && (
        <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
          <img
            src={settings.home_image_url}
            alt={homeTitle}
            className="w-full h-48 sm:h-64 object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}

      {/* Greeting + Title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{greeting}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            {homeTitle}
          </h1>
        </div>
        {isAdmin() && (
          <Link to="/settings">
            <Button variant="outline" size="sm" data-testid="edit-homepage-btn">
              <SettingsIcon size={14} className="mr-1" /> Edit Homepage
            </Button>
          </Link>
        )}
      </div>

      {/* Club content from settings */}
      {hasCustomContent ? (
        <Card>
          <CardContent className="pt-6">
            <div className="prose max-w-none">
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {linkify(settings.home_content)}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : isAdmin() ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <HomeIcon className="mx-auto text-slate-300 mb-3" size={36} />
            <p className="text-slate-700 font-medium">No homepage content yet.</p>
            <p className="text-slate-500 text-sm mt-1">
              Open <strong>Settings → Homepage</strong> to add a welcome message, schedule, or club news.
            </p>
            <Link to="/settings" className="inline-block mt-4">
              <Button className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                <SettingsIcon size={16} className="mr-1" /> Add Homepage Content
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : null}

      {/* Shooter status card — visible to anyone without an active linked shooter */}
      {(!user?.linked_shooter_id || user?.shooter_status === "pending") && (
        <Card className={user?.shooter_status === "pending" ? "border-amber-300 bg-amber-50/40" : "border-[#1B3B6F]/30"}>
          <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {user?.shooter_status === "pending" ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="text-amber-700" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Shooter application pending</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Your shooter profile is awaiting admin approval. You'll be able to enter scores and view "My Results" once approved.
                    </p>
                  </div>
                </div>
                <Badge className="bg-amber-200 text-amber-900">Awaiting approval</Badge>
              </>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-[#1B3B6F]/10 rounded-lg">
                    <UserPlus className="text-[#1B3B6F]" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Want to compete? Register as a Shooter.</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Submit a quick application to link your account to a shooter profile. An admin will approve it.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShooterDialogOpen(true)}
                  className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  data-testid="apply-shooter-btn"
                >
                  <UserPlus size={16} className="mr-2" />
                  Register as a Shooter
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation that the user IS an approved shooter (small badge) */}
      {user?.linked_shooter_id && user?.shooter_status !== "pending" && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <CheckCircle size={14} className="text-green-600" />
          Linked to shooter profile{user?.shooter_name ? `: ${user.shooter_name}` : ""}.
        </div>
      )}

      {/* Today's Events */}
      {!loading && todayData.events && todayData.events.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Calendar className="text-[#1B3B6F]" />
              Today's Events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {todayData.events.map((ev) => (
              <Link
                key={ev.id}
                to={`/event-info/${ev.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors"
                data-testid={`home-today-event-${ev.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {ev.cover_photo_url ? (
                    <img
                      src={ev.cover_photo_url}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="p-2 bg-[#1B3B6F]/10 text-[#1B3B6F] rounded-lg flex-shrink-0">
                      <Target size={18} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{ev.name}</p>
                    <p className="text-xs text-slate-500">{(ev.matches?.length || 0)} matches scheduled</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
              </Link>
            ))}
            <div className="pt-2 flex gap-2 flex-wrap">
              <Link to="/today"><Button variant="outline" size="sm">View Today's Events</Button></Link>
              <Link to="/todays-matches"><Button variant="outline" size="sm">Today's Matches</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <CalendarClock className="text-[#1B3B6F]" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : upcomingEvents.length === 0 ? (
              <p className="text-sm text-slate-500">No upcoming events. Check back soon.</p>
            ) : (
              <div className="space-y-2">
                {upcomingEvents.map((ev) => (
                  <Link
                    key={ev.id}
                    to={`/event-info/${ev.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors"
                    data-testid={`home-upcoming-event-${ev.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {ev.cover_photo_url && (
                        <img
                          src={ev.cover_photo_url}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{ev.name}</p>
                        <p className="text-xs text-slate-500">
                          {formatDate(ev.start_date)}
                          {ev.end_date && ev.end_date !== ev.start_date ? ` – ${formatDate(ev.end_date)}` : ""}
                        </p>
                      </div>
                    </div>
                    <Info size={16} className="text-slate-400 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            )}
            <div className="pt-3">
              <Link to="/events">
                <Button variant="outline" size="sm">View All Events</Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Megaphone className="text-[#1B3B6F]" />
              Latest Announcements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : announcements.length === 0 ? (
              <p className="text-sm text-slate-500">No announcements yet.</p>
            ) : (
              <div className="space-y-3">
                {announcements.map((a) => (
                  <div key={a.id} className="p-3 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-800">{a.title || "Announcement"}</p>
                      {a.priority === "high" && (
                        <Badge className="bg-red-100 text-red-800">Important</Badge>
                      )}
                    </div>
                    {a.message && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-3 whitespace-pre-wrap">{a.message}</p>
                    )}
                    {a.created_at && (
                      <p className="text-xs text-slate-400 mt-1">{formatDate(a.created_at)}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="pt-3">
              <Link to="/announcements">
                <Button variant="outline" size="sm">View All Announcements</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-lg">Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link to="/leaderboard">
              <Button variant="outline" className="w-full justify-start">
                <Trophy size={16} className="mr-2 text-amber-600" />
                Leaderboard
              </Button>
            </Link>
            <Link to="/match-calendar">
              <Button variant="outline" className="w-full justify-start">
                <Calendar size={16} className="mr-2 text-[#1B3B6F]" />
                Calendar
              </Button>
            </Link>
            <Link to="/my-results">
              <Button variant="outline" className="w-full justify-start">
                <Target size={16} className="mr-2 text-green-700" />
                My Results
              </Button>
            </Link>
            <Link to="/shooters">
              <Button variant="outline" className="w-full justify-start">
                <Info size={16} className="mr-2 text-slate-700" />
                Shooters
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Shooter application dialog */}
      <Dialog open={shooterDialogOpen} onOpenChange={setShooterDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <UserPlus className="text-[#1B3B6F]" />
              Register as a Shooter
            </DialogTitle>
            <DialogDescription>
              Your application will be reviewed by an admin. You'll see "Approved" here once they activate your shooter profile.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleApplyAsShooter} className="space-y-4 mt-2">
            <div className="text-sm text-slate-600 bg-slate-50 rounded-md p-3">
              <p><span className="text-slate-500">Name:</span> <strong>{user?.name || "(your account name)"}</strong></p>
              {user?.email && <p><span className="text-slate-500">Email:</span> {user.email}</p>}
            </div>

            <div>
              <Label htmlFor="shooter_id_number">Member / Shooter ID Number (optional)</Label>
              <Input
                id="shooter_id_number"
                data-testid="apply-shooter-id-input"
                value={shooterForm.shooter_id}
                onChange={(e) => setShooterForm({ ...shooterForm, shooter_id: e.target.value })}
                placeholder="e.g. 12345"
              />
            </div>

            <div>
              <Label htmlFor="apply_phone">Phone (optional)</Label>
              <Input
                id="apply_phone"
                value={shooterForm.phone}
                onChange={(e) => setShooterForm({ ...shooterForm, phone: e.target.value })}
                placeholder="555-555-5555"
              />
            </div>

            <div>
              <Label htmlFor="apply_pin">4-digit PIN (optional)</Label>
              <p className="text-xs text-slate-500 mb-1">Used when entering your own scores on the range. You can set this later.</p>
              <Input
                id="apply_pin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                value={shooterForm.pin}
                onChange={(e) => setShooterForm({ ...shooterForm, pin: e.target.value.replace(/\D/g, "") })}
                placeholder="••••"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
              disabled={shooterSubmitting}
              data-testid="submit-shooter-application-btn"
            >
              {shooterSubmitting ? "Submitting…" : "Submit Application"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
